import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import { FileTypeEnum } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import UploadFileInput from '@repo/ui/file-input'
import { DownloadFile, VideoIcon } from '@repo/ui/icons'
import TimeInput from '@repo/ui/time-input'
import Typography from '@repo/ui/typography'
import { useFormContext } from 'react-hook-form'
import { toast } from 'sonner'

import { ScoreCardInput } from '../../../../lib/types/models/score-card/score-card-input'
import { StaffApplicationLight } from '../../../../lib/types/models/staff-application/staff-application'
import { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'

interface PcrScoreCardFormInterviewTranscriptDetailsProps {
  staffApplication: StaffApplicationLight
  disabled?: boolean
}

type PcrScoreCardFormInterviewScheduleProps = {
  disabled?: boolean
}

export function PcrScoreCardFormInterviewSchedule({
  disabled
}: PcrScoreCardFormInterviewScheduleProps) {
  const form = useFormContext<ScoreCardInput>()

  const { interviewStartHour, interviewStartMinute } = form.watch()

  return (
    <div className={'flex flex-col gap-2'}>
      <Typography variant={'label-semibold'}>Date et heure de l'entretien</Typography>
      <div className={'flex flex-row gap-5 border border-grey-light rounded-lg p-4'}>
        <div className={'flex flex-col flex-1 gap-2'}>
          <Typography variant={'label-semibold'} className={'text-grey-lighter'}>
            Sélectionner la date
          </Typography>

          <FormField
            control={form.control}
            name={'interviewStartDate'}
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    disabled={disabled}
                    label="Sélectionner la date"
                    mode="over"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className={'flex flex-col gap-2 w-full max-w-52'}>
          <FormField
            control={form.control}
            name="interviewStartHour"
            render={() => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <TimeInput
                    disabled={disabled}
                    label={"Sélectionner l'heure"}
                    onChange={(value) => {
                      const [hours, minutes] = value.split(':')
                      form.setValue('interviewStartHour', Number.parseInt(hours))
                      form.setValue('interviewStartMinute', Number.parseInt(minutes))
                    }}
                    value={DateFormatter.formatTimeString(
                      interviewStartHour,
                      interviewStartMinute
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  )
}

export function PcrScoreCardFormInterviewTranscriptDetails({
  disabled,
  staffApplication
}: PcrScoreCardFormInterviewTranscriptDetailsProps) {
  const form = useFormContext<ScoreCardInput>()

  const { interviewStartDate, interviewStartHour, interviewStartMinute } = form.watch()

  return (
    <div className={'flex flex-col gap-2'}>
      <Typography variant={'label-semibold'}>Transcript de l'entretien</Typography>
      <div className={'flex flex-col gap-5 border border-grey-light rounded-lg p-4'}>
        <div className={'flex justify-between items-center w-full'}>
          <Typography variant={'tag'}>
            {toFullName(staffApplication.firstName, staffApplication.lastName)}
          </Typography>
          <Badge
            variant={'primary'}
            text={`${interviewStartDate ? DateFormatter.DDMMYYYY(interviewStartDate) : '-'} ${DateFormatter.HH(interviewStartHour)}:${DateFormatter.MM(interviewStartMinute)}`}
          />
        </div>

        {/* file inputs (video and transcript)*/}
        <div className={'flex flex-row gap-3'}>
          <div className={'w-1/2'}>
            <FormField
              control={form.control}
              name={'video'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <UploadFileInput
                      placeholder="Glisser-déposer le fichier vidéo"
                      types={[FileTypeEnum.VIDEO]}
                      icon={VideoIcon}
                      label={'Télécharger la vidéo'}
                      files={field?.value ? [field?.value] : undefined}
                      previewOnClick
                      onFilesChange={(files) => {
                        field.onChange(files.length === 0 ? null : files[0])
                      }}
                      onFileError={(error) => toast.error(error)}
                      disabled={disabled}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className={'w-1/2'}>
            <FormField
              control={form.control}
              name={'transcript'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <UploadFileInput
                      placeholder="Glisser-déposer le fichier de transcription"
                      types={[FileTypeEnum.PDF]}
                      icon={DownloadFile}
                      label={'Télécharger la transcription'}
                      files={field?.value ? [field?.value] : undefined}
                      previewOnClick
                      onFilesChange={(files) =>
                        field.onChange(files.length === 0 ? null : files[0])
                      }
                      onFileError={(error) => toast.error(error)}
                      disabled={disabled}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
